import { getLoginContext } from '@athena-lib/loginContext.svelte.js';
import type { CheckoutStateData } from '@stickerapp-org/checkout-sdk';
import type { CheckoutErrors } from '@athena-lib/Checkout/Checkout.svelte';
import { getCheckoutSdk } from '$lib/checkout.svelte';

type FormInteractionEvent =
  | 'form_error'
  | 'form_shown'
  | 'form_started'
  | 'form_completed'
  | 'form_submitted';
type UserTypes = 'business' | 'private';
export const getCheckoutGtmStateMachine = () => {
  const submittedUniqueEvents: Array<FormInteractionEvent> = [];
  let userType: UserTypes = 'private';
  const submitUniqueEvent = (eventType: FormInteractionEvent) => {
    if (!submittedUniqueEvents.includes(eventType)) {
      triggerGtmFormInterActionEvent(eventType, userType);
      submittedUniqueEvents.push(eventType);
    }
  };
  let previousErrors: Array<string> = [];
  submitUniqueEvent('form_shown');
  return {
    setUserType: (newUserType: UserTypes) => {
      userType = newUserType;
    },
    reportFormErrors: (checkoutForm: CheckoutErrors) => {
      // we don't want to report already reported errors
      const currentErrors = collectErrors(checkoutForm);
      const newErrors = currentErrors.filter((error) => !previousErrors.includes(error));
      previousErrors = currentErrors;
      newErrors.forEach((error) => {
        triggerGtmFormInterActionEvent('form_error', userType, error);
      });
    },
    // any field changed
    startForm: () => submitUniqueEvent('form_started'),
    // the form is valid
    completeForm: () => {
      submitUniqueEvent('form_started');
      submitUniqueEvent('form_completed');
    },
    submitForm: () => {
      submitUniqueEvent('form_started');
      submitUniqueEvent('form_completed');
      submitUniqueEvent('form_submitted');
    },
  };
};

export const reportInvalidGiftCardOrPromoCode = (user_type: UserTypes) => {
  triggerGtmFormInterActionEvent('form_error', user_type, 'invalid gift card or promo code');
};

const collectErrors = (
  checkoutErrors: Record<string, unknown>,
  path: Array<string> = [],
): Array<string> => {
  const values = Object.entries(checkoutErrors);
  return values.flatMap(([key, value]) => {
    if (typeof value === 'object' && value !== null) {
      return collectErrors(value as Record<string, unknown>, [...path, key]);
    }
    if (typeof value === 'string') {
      return [[[...path, key].join('.'), value].join(': ') satisfies string];
    }
    return [];
  });
};

function triggerGtmFormInterActionEvent(
  interactionType: FormInteractionEvent,
  user_type: UserTypes,
  errorType?: unknown,
) {
  const signedIn = getLoginContext().isLoggedIn;
  const eventParams: Record<string, string> = {
    interaction_type: interactionType,
    form_type: 'checkout_form',
    interaction_category: 'form',
    checkout_version: '2',
    checkout_user_type: user_type,
    signed_in: signedIn.toString(),
  };

  if (errorType) {
    eventParams.error_type = typeof errorType === 'string' ? errorType : JSON.stringify(errorType);
  }

  document.dispatchEvent(
    new CustomEvent('gtm-event', {
      detail: {
        event: 'form_interaction',
        event_params: eventParams,
      },
    }),
  );
}

export const getCartEventsTracker = () => {
  const getCheckoutState = async (): Promise<CheckoutStateData> => {
    const sdk = getCheckoutSdk();
    if (!sdk) {
      return Promise.reject('no sdk');
    }
    if (!sdk.state.checkout) {
      await sdk.actions.loadCheckout();
    }
    return sdk.state;
  };

  const getBaseProps = async (
    checkoutLineItems?: CheckoutStateData['lines'],
  ): Promise<BaseEventProps> => {
    const checkoutState = await getCheckoutState();
    return {
      checkout_version: 2,
      currency: checkoutState.currency,
      value: (checkoutState.checkout?.leftToPayInclTax ?? 0) / 100,
      page_source: window.location.pathname, // internationalized page name?
      items: (checkoutLineItems ?? checkoutState.lines).map((x, i) =>
        toGtmEcommerceItem(x, i, checkoutState.currency),
      ),
    };
  };

  const createEventConstructor =
    (eventName: EnhancedCommerceEventType) =>
    async (checkoutLineItems?: CheckoutStateData['lines']) =>
      document.dispatchEvent(
        new CustomEvent('gtm-event', {
          detail: {
            event: eventName,
            ecommerce: {
              ...(await getBaseProps(checkoutLineItems)),
            } satisfies EnhancedCommerceEvent,
          },
        }),
      );

  const addToCart = createEventConstructor('add_to_cart');
  const removeFromCart = createEventConstructor('remove_from_cart');

  let latestLineItems: CheckoutStateData['lines'] | undefined = undefined;

  return {
    viewCart: createEventConstructor('view_cart'),
    beginCheckout: createEventConstructor('begin_checkout'),
    reportUpdatedLineItems: (newLineItems: CheckoutStateData['lines']) => {
      // sets the initial value
      if (latestLineItems === undefined) {
        const sdkLines = getCheckoutSdk()?.state;

        if (!sdkLines?.isLoading && sdkLines?.lines) {
          latestLineItems = sdkLines.lines;
        }
        return;
      }
      const oldLineItems = latestLineItems;

      // find the ones (by id AND quantity) that are different, they should generate removed and added events
      const addedLineItems = newLineItems.filter(
        (newItem) =>
          oldLineItems.findIndex(
            (oldItem) => oldItem.id === newItem.id && oldItem.quantity === newItem.quantity,
          ) === -1,
      );
      const removedLineItems = oldLineItems.filter(
        (oldItem) =>
          newLineItems.findIndex(
            (newItem) => oldItem.id === newItem.id && oldItem.quantity === newItem.quantity,
          ) === -1,
      );

      if (removedLineItems.length > 0) {
        removeFromCart(removedLineItems);
      }

      if (addedLineItems.length > 0) {
        addToCart(addedLineItems);
      }

      latestLineItems = newLineItems;
    },

    purchase: async () => {
      const checkoutState = await getCheckoutState();
      const shipping =
        checkoutState.checkout?.shipping.selectedShippingMethod?.price.total.amountInclTax ?? 0;

      const tax =
        (checkoutState.checkout?.total.amountInclTax ?? 0) -
        (checkoutState.checkout?.total.amountExclTax ?? 0);

      const basePrams = {
        type: 'purchase',
        transaction_id: '',
        shipping,
        tax,
        coupon: checkoutState.discountCodes.at(0)?.code ?? '',
        ...(await getBaseProps()),
      } satisfies EnhancedCommerceEvent;
      return (orderId?: string) => {
        document.dispatchEvent(
          new CustomEvent('gtm-event', {
            detail: {
              event: 'purchase' satisfies EnhancedCommerceEventType,
              ecommerce: { ...basePrams, transaction_id: orderId },
            },
          }),
        );
      };
    },
  };
};

type EnhancedCommerceEventType =
  | 'add_to_cart'
  | 'view_cart'
  | 'purchase'
  | 'remove_from_cart'
  | 'begin_checkout';

type BaseEventProps = {
  checkout_version: 2;
  currency: string;
  value: number;
  page_source: string;
  items: Array<EcommerceItem>;
};
type EnhancedCommerceEvent = (
  | {
      type: 'purchase';
      transaction_id: string;
      tax: number;
      shipping: number;
      coupon: string;
    }
  | object
) &
  BaseEventProps;

export const toGtmEcommerceItem = (
  lineItem: CheckoutStateData['lines'][number],
  index: number,
  currency: string,
): EcommerceItem => {
  const price = lineItem.price.unit.amountInclTax / 100;

  switch (lineItem.content.type) {
    case 'giftCard': {
      return {
        currency,
        index,
        price,
        item_category: 'library_item',
        item_category2: 'stickers',
        item_category3: 'singles',
        item_category4: '',
        item_category5: '',
        item_name: 'giftcard',
        item_id: lineItem.content.figureId,
        item_brand: 'StickerApp',
        item_variant: 'special',
        quantity: lineItem.quantity,
      };
    }
    case 'stickerPack': {
      return {
        currency,
        index,
        price,
        item_category: 'library_item',
        item_category2: 'stickers',
        item_category3: 'singles',
        item_category4: '',
        item_category5: '',
        item_name: [lineItem.content.productName, lineItem.content.productFamilyName].join(' '),
        item_id: String(lineItem.content.stickerPackId),
        item_brand: 'StickerApp',
        item_variant: 'sticker_pack',
        quantity: lineItem.quantity,
      };
    }

    case 'customSticker': {
      const dimensions =
        !isNaN(Number(lineItem.content.attributes.width_mm)) &&
        !isNaN(Number(lineItem.content.attributes.height_mm))
          ? `${Math.round(Number(lineItem.content.attributes.width_mm) / 10)}x${Math.round(
              Number(lineItem.content.attributes.height_mm) / 10,
            )}`
          : '';

      return {
        currency,
        price,
        item_brand: 'StickerApp',
        item_id: String(lineItem.content.customStickerId),
        item_variant: String(lineItem.content.attributes['material']),
        item_category: lineItem.content.name === 'library_item' ? 'custom' : 'library_item',
        item_category2: 'stickers',
        item_category3: String(lineItem.content.attributes['laminate']),
        item_category4: dimensions,
        item_category5: String(lineItem.content.attributes['sheet_name']),
        item_list_name: 'Sticker Products',
        item_list_id: 'product_list',
        item_name: lineItem.content.productName,
        quantity: lineItem.quantity,
        index,
      } as const;
    }
  }
};

export type EcommerceItem = {
  item_id: string;
  item_name: string;
  item_category: 'custom' | 'library_item';
  item_brand: 'StickerApp';
  item_category2: 'stickers';
  item_category3: string; // laminate
  item_category4: string;
  item_category5: string;
  item_variant: string;
  item_list_name?: 'Sticker Products';
  item_list_id?: 'product_list';
  price: number;
  quantity: number;
  currency: string;
  index: number;
};
